// ── Forest Synthesis Panel ────────────────────────────────────
// The 🌉 SYNTHESIS tab in Forest mode. Shows cross-canvas blends —
// emergent ideas produced by Phase B's recombine.blend operator
// applied to high-novelty seeds from DIFFERENT canvases.
//
// See features/ai-workflows/forest-cross-canvas-blending.feature

import React from 'react';
import { useForest } from './ForestContext';
import { useNovelty } from './NoveltyContext';
import { CompetitionBadge, VerifyNoveltyPanel } from './CompetitionUI';

function noveltyColor(score) {
  if (score == null) return 'var(--text-muted)';
  if (score >= 70) return 'var(--fitness-excellent)';
  if (score >= 50) return 'var(--fitness-good)';
  if (score >= 35) return 'var(--fitness-fair)';
  if (score >= 20) return 'var(--fitness-weak)';
  return 'var(--fitness-poor)';
}

function noveltyLabel(score) {
  if (score == null) return 'unscored';
  if (score >= 90) return 'Reframes';
  if (score >= 70) return 'Notable';
  if (score >= 50) return 'Variant';
  if (score >= 30) return 'Common';
  return 'Cliché';
}

export default function ForestSynthesis() {
  const {
    forestCanvases,
    crossBlends,
    crossBlendChampion,
    crossBlendStatus,
    runCrossCanvasBlend,
  } = useForest();
  const novelty = useNovelty();

  const scoredCanvasCount = forestCanvases.filter(c => c.status === 'ready' && c.nodes?.length).length;
  const canRun = scoredCanvasCount >= 2 && crossBlendStatus !== 'running';
  const sortedBlends = [...crossBlends].sort((a, b) => (b.novelty ?? 0) - (a.novelty ?? 0));

  return (
    <div style={{
      padding: 24,
      maxWidth: 920,
      margin: '0 auto',
      fontFamily: 'var(--font-mono)',
      color: 'var(--text-primary)',
      overflowY: 'auto',
      height: '100%',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🌉 Cross-canvas synthesis</h1>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {scoredCanvasCount}/{forestCanvases.length} canvases ready
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
        Splice high-novelty ideas from different canvases. The ideas you wouldn't have reached
        by thinking inside any single canvas alone.
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 24 }}>
        <button
          onClick={() => runCrossCanvasBlend({ count: 5 })}
          disabled={!canRun}
          aria-label={canRun ? 'Run cross-canvas blending' : 'Need at least 2 ready canvases to run'}
          style={{
            background: canRun ? 'var(--accent)' : 'var(--bg-card)',
            color: canRun ? 'var(--text-primary)' : 'var(--text-muted)',
            border: 'none',
            padding: '8px 12px',
            borderRadius: 4,
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            cursor: canRun ? 'pointer' : 'not-allowed',
            minHeight: 36,
            transition: 'background 0.15s ease, opacity 0.15s ease',
          }}
        >
          {crossBlendStatus === 'running'
            ? '⏳ Splicing…'
            : crossBlends.length > 0
              ? '🔄 Regenerate blends'
              : '▶ Run cross-canvas blending'}
        </button>
        {crossBlends.length > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {crossBlends.length} blend{crossBlends.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Empty / gating states */}
      {scoredCanvasCount < 2 && (
        <div style={{
          padding: 16,
          background: 'var(--bg-secondary)',
          border: '1px dashed var(--border)',
          borderRadius: 8,
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}>
          Need at least 2 generated canvases. Open the canvas tabs and run "Generate All" first.
        </div>
      )}

      {scoredCanvasCount >= 2 && crossBlends.length === 0 && crossBlendStatus !== 'running' && (
        <div style={{
          padding: 16,
          background: 'var(--bg-secondary)',
          border: '1px dashed var(--border)',
          borderRadius: 8,
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}>
          No cross-canvas blends yet. Click "Run cross-canvas blending" to splice high-novelty
          ideas from different canvases into emergent cross-cutting bets.
        </div>
      )}

      {/* Champion banner */}
      {crossBlendChampion && (
        <div
          role="region"
          aria-label={`Forest champion: ${crossBlendChampion.label}`}
          style={{
            background: 'linear-gradient(135deg, #1a2e1a 0%, #0e1f0e 100%)',
            border: `1px solid ${noveltyColor(crossBlendChampion.novelty)}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 8 }}>
            🏆 FOREST CHAMPION · novelty {crossBlendChampion.novelty}/100 · {noveltyLabel(crossBlendChampion.novelty)}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
            {crossBlendChampion.label}
          </div>
          <CanvasLineageChips canvasTitles={crossBlendChampion.sourceCanvasTitles || []} />
          {crossBlendChampion.blendStrategy && (
            <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--info)', margin: '10px 0' }}>
              {crossBlendChampion.blendStrategy}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 12 }}>
            {crossBlendChampion.reasoning}
          </div>
          {/* Tier 2 badge (no-op if forest auto-scan didn't fire) + Tier 3 verify */}
          <CompetitionBadge candidateId={`forest-champion`} />
          <VerifyNoveltyPanel candidate={crossBlendChampion} candidateId={`forest-champion`} />
          {novelty?.onDrillNovelty && (
            <button
              onClick={() => novelty.onDrillNovelty(crossBlendChampion.label, crossBlendChampion.reasoning, 'forest champion')}
              aria-label={`Drill into forest champion: ${crossBlendChampion.label}`}
              style={{
                background: 'var(--bg-card)', color: 'var(--success)',
                border: '1px solid var(--success)', padding: '8px 12px', fontSize: 11,
                borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-mono)', minHeight: 32,
                transition: 'background 0.15s ease',
              }}
            >
              🔬 Drill into champion
            </button>
          )}
        </div>
      )}

      {/* Blend cards */}
      {sortedBlends.length > 0 && (
        <div role="list" aria-label="Cross-canvas blends" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedBlends.map((b, i) => (
            <BlendCard key={i} blend={b} blendId={`forest-blend-${i}`} onDrill={novelty?.onDrillNovelty} />
          ))}
        </div>
      )}
    </div>
  );
}

function BlendCard({ blend, blendId, onDrill }) {
  const score = blend.novelty;
  const isError = !!blend.error;
  return (
    <div
      role="listitem"
      style={{
        background: 'var(--bg-card)',
        border: `1px solid ${isError ? 'var(--error)' : 'var(--border)'}`,
        borderLeft: `4px solid ${noveltyColor(score)}`,
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <span
          aria-label={`Novelty score ${score ?? 'unscored'} of 100`}
          style={{
            fontSize: 18, fontWeight: 700, color: noveltyColor(score),
            minWidth: 28, textAlign: 'right',
          }}
        >
          {score ?? '—'}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
            {blend.label || (isError ? `(blend failed: ${blend.error})` : 'Untitled blend')}
          </div>
          <CanvasLineageChips canvasTitles={blend.sourceCanvasTitles || []} />
        </div>
      </div>

      {blend.blendStrategy && (
        <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--info)', marginBottom: 6 }}>
          {blend.blendStrategy}
        </div>
      )}

      {blend.reasoning && (
        <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 8 }}>
          {blend.reasoning}
        </div>
      )}

      {blend.parents?.length === 2 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)', paddingTop: 8, lineHeight: 1.5 }}>
          <div>from <strong style={{ color: 'var(--text-secondary)' }}>{blend.parents[0].canvasTitle}</strong>: "{(blend.parents[0].label || '').slice(0, 80)}"</div>
          <div>× <strong style={{ color: 'var(--text-secondary)' }}>{blend.parents[1].canvasTitle}</strong>: "{(blend.parents[1].label || '').slice(0, 80)}"</div>
        </div>
      )}

      {/* Tier 2 badge (no-op if no auto-scan) + Tier 3 verify button */}
      {!isError && (
        <>
          <CompetitionBadge candidateId={blendId} compact />
          <VerifyNoveltyPanel candidate={blend} candidateId={blendId} />
        </>
      )}

      {onDrill && !isError && (
        <button
          onClick={() => onDrill(blend.label, blend.reasoning, 'forest blend')}
          aria-label={`Drill into blend: ${blend.label}`}
          style={{
            marginTop: 12, background: 'var(--bg-secondary)', color: 'var(--success)',
            border: '1px solid var(--success)', padding: '4px 8px', fontSize: 11,
            borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-mono)', minHeight: 28,
            transition: 'background 0.15s ease',
          }}
        >
          🔬 Drill into this blend
        </button>
      )}
    </div>
  );
}

function CanvasLineageChips({ canvasTitles }) {
  if (!canvasTitles || canvasTitles.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
      {canvasTitles.map((t, i) => (
        <React.Fragment key={i}>
          <span style={{
            background: 'var(--bg-card-elevated)',
            color: 'var(--info)',
            padding: '2px 8px',
            borderRadius: 12,
            border: '1px solid var(--border)',
          }}>{t}</span>
          {i < canvasTitles.length - 1 && (
            <span style={{ color: 'var(--text-muted)' }} aria-hidden="true">×</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
